/**
 * Store client : réplique de l'état serveur (snapshot + deltas) et données
 * dérivées calculées localement (géométrie des remontées, tracés des pistes,
 * ordre des files d'attente). Le rendu lit le store à chaque image ; le HUD
 * s'y abonne avec une fréquence réduite.
 */
import {
  PisteNetwork,
  Replica,
  VAL_CENDREE,
  MAPS,
  computeLiftGeometry,
  getHeightfield,
  makePisteTrack,
  type AckMsg,
  type Command,
  type DeltaMsg,
  type GameEvent,
  type GameMap,
  type Heightfield,
  type LiftGeometry,
  type PisteTrack,
  type ReplicaPart,
  type SnapshotMsg,
  type WelcomeMsg,
} from '@snowchain/shared';
import { ServerClock } from '../net/clock';
import type { NetEvent, NetStatus } from '../net/net-core';

type Listener = (parts: Set<ReplicaPart | 'net'>) => void;

export class ClientStore {
  replica: Replica | null = null;
  welcome: WelcomeMsg | null = null;
  readonly clock = new ServerClock();
  map: GameMap = VAL_CENDREE;
  hf: Heightfield = getHeightfield(VAL_CENDREE.features);
  status: NetStatus = 'connecting';
  statusDetail = '';
  rtt = 0;
  traffic = { bytesPerSec: 0, msgsPerSec: 0 };
  /** Compteurs de version par partie (le rendu détecte les changements). */
  readonly versions: Record<ReplicaPart, number> = { clock: 0, world: 0, station: 0, lifts: 0, pistes: 0, buildings: 0, agents: 0, groomers: 0, me: 0, usage: 0 };
  private liftGeo = new Map<number, { key: string; geo: LiftGeometry }>();
  private tracks = new Map<number, { key: string; track: PisteTrack }>();
  /** Rang de chaque skieur dans sa file d'attente (remontée ou comptoir). */
  readonly queueRank = new Map<number, number>();
  private queuesVersion = -1;
  private listeners = new Set<Listener>();
  /** Événements ponctuels non encore affichés (toasts). */
  readonly events: GameEvent[] = [];
  sendCommand: (cmd: Command) => Promise<AckMsg> = async () => ({ t: 'ack', seq: 0, ok: false, error: 'Hors ligne' });

  get isOwner(): boolean {
    return !!this.replica && !!this.welcome && this.replica.station.ownerId === this.welcome.playerId;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private notify(parts: Set<ReplicaPart | 'net'>): void {
    for (const fn of this.listeners) fn(parts);
  }

  handle(e: NetEvent): void {
    switch (e.k) {
      case 'welcome':
        this.welcome = e.msg;
        break;
      case 'snapshot':
        this.applySnapshot(e.msg, e.at);
        break;
      case 'delta':
        this.applyDelta(e.msg, e.at);
        break;
      case 'status':
        this.status = e.status;
        this.statusDetail = e.detail ?? '';
        this.notify(new Set(['net']));
        break;
      case 'rtt':
        this.rtt = this.rtt ? this.rtt * 0.7 + e.rtt * 0.3 : e.rtt;
        break;
      case 'traffic':
        this.traffic = { bytesPerSec: e.bytesPerSec, msgsPerSec: e.msgsPerSec };
        this.notify(new Set(['net']));
        break;
      case 'error':
        this.events.push({ k: 'notice', level: 'danger', title: 'Serveur', message: e.msg.message });
        this.notify(new Set(['net']));
        break;
      case 'ack':
        break;
    }
  }

  private applySnapshot(msg: SnapshotMsg, at: number): void {
    this.replica = new Replica(msg);
    this.map = MAPS[msg.world.mapId] ?? VAL_CENDREE;
    this.hf = getHeightfield(this.map.features);
    this.clock.sample(msg.clock, at);
    for (const k of Object.keys(this.versions) as ReplicaPart[]) this.versions[k]++;
    this.notify(new Set(Object.keys(this.versions) as ReplicaPart[]));
  }

  private applyDelta(msg: DeltaMsg, at: number): void {
    if (!this.replica) return;
    const changed = this.replica.apply(msg);
    if (!changed) return;
    this.clock.sample(msg.clock, at);
    for (const p of changed) this.versions[p]++;
    if (msg.events) this.events.push(...msg.events);
    this.notify(changed);
  }

  /* Données dérivées ------------------------------------------------- */

  liftGeometry(id: number): LiftGeometry | null {
    const l = this.replica?.lifts.get(id);
    if (!l) return null;
    const key = `${l.kind}:${l.bottom.join(',')}:${l.top.join(',')}`;
    const c = this.liftGeo.get(id);
    if (c && c.key === key) return c.geo;
    const geo = computeLiftGeometry(this.hf, l.kind, l.bottom, l.top);
    this.liftGeo.set(id, { key, geo });
    return geo;
  }

  pisteTrack(id: number): PisteTrack | null {
    const p = this.replica?.pistes.get(id);
    if (!p) return null;
    // Empreinte du tracé entier : une reprise peut garder ses extrémités et son nombre de points.
    let h = 0;
    for (const q of p.path) h = (h * 31 + Math.round(q[0] * 10) * 7 + Math.round(q[1] * 10)) | 0;
    const key = `${p.width}:${p.path.length}:${h}`;
    const c = this.tracks.get(id);
    if (c && c.key === key) return c.track;
    const track = makePisteTrack(this.hf, id, p.path, p.width);
    this.tracks.set(id, { key, track });
    return track;
  }

  private netCache: { key: string; net: PisteNetwork } | null = null;

  /**
   * Réseau du domaine (raccords, accessibilité), recalculé seulement quand
   * un tracé, une ouverture de piste ou l'état d'une remontée change.
   */
  network(): PisteNetwork | null {
    const r = this.replica;
    if (!r) return null;
    let key = '';
    for (const p of r.pistes.values()) key += `${p.id}:${p.name}:${p.width}:${p.difficulty}:${p.open ? 1 : 0}:${(this.pisteTrack(p.id)?.length ?? 0).toFixed(1)}|`;
    for (const l of r.lifts.values()) key += `L${l.id}:${l.status}|`;
    if (this.netCache && this.netCache.key === key) return this.netCache.net;
    const net = new PisteNetwork({
      hf: this.hf,
      map: this.map,
      lifts: r.lifts.values(),
      pistes: r.pistes.values(),
      liftGeo: (l) => this.liftGeometry(l.id) as LiftGeometry,
      track: (p) => this.pisteTrack(p.id) as PisteTrack,
    });
    this.netCache = { key, net };
    return net;
  }

  /** Recalcule le rang de chacun dans les files (ordre d'arrivée, comme le serveur). */
  refreshQueues(): void {
    if (!this.replica || this.queuesVersion === this.versions.agents) return;
    this.queuesVersion = this.versions.agents;
    this.queueRank.clear();
    // Files des remontées (clé positive) et des comptoirs (clé négative).
    const byLine = new Map<number, [number, number][]>();
    for (const a of this.replica.agents.values()) {
      if (a.m[0] !== 1 && a.m[0] !== 6) continue;
      const key = a.m[0] === 1 ? a.m[2] : -a.m[2];
      const list = byLine.get(key) ?? [];
      list.push([a.m[1], a.i]);
      byLine.set(key, list);
    }
    for (const list of byLine.values()) {
      list.sort((p, q) => p[0] - q[0] || p[1] - q[1]);
      list.forEach(([, id], rank) => this.queueRank.set(id, rank));
    }
  }
}
